import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AvatarIcon, CountryFlag, GlobeInline } from "@/lib/identity";
import { PremiumBadge } from "@/components/PremiumBadge";
import { LivePill } from "@/components/LivePill";
import { useLivePoll } from "@/lib/useLivePoll";
import CountryWar from "@/components/CountryWar";
import SeasonTimeline from "@/components/SeasonTimeline";
import { Crown, Fire, CheckCircle, Circle, Trophy } from "@phosphor-icons/react";

// Blueprint page 9 — 3 primary board scopes + per-country drill-down.
const TABS = [
  { v: "AFRICA", l: "Africa", testid: "africa" },
  { v: "ROW", l: "Rest of World", testid: "row" },
  { v: "COUNTRY", l: "By country", testid: "country" },
];

// View toggle — season standings vs today's most active
const VIEWS = [
  { v: "SEASON", l: "Season standings", testid: "season" },
  { v: "ACTIVE", l: "Most active today", testid: "active-today" },
];

export default function Leaderboard() {
  const [view, setView] = useState("SEASON");
  const [tab, setTab] = useState("AFRICA");
  const [country, setCountry] = useState("NG");           // used only when tab=COUNTRY
  const [countries, setCountries] = useState([]);

  // Load country list once
  useEffect(() => {
    api.get("/countries")
      .then(({ data }) => setCountries(data?.countries || []))
      .catch(() => setCountries([]));
  }, []);

  // Silent polling for live rankings (30s, pauses when tab hidden)
  const { data, updatedAt, busy, refresh } = useLivePoll(
    async () => {
      let q;
      if (tab === "AFRICA") q = "scope=africa";
      else if (tab === "ROW") q = "scope=row";
      else q = `country=${country}`;
      const url = view === "ACTIVE"
        ? `/season/leaderboard/active-today?${q}&limit=50`
        : `/season/leaderboard?${q}`;
      const r = await api.get(url);
      return r.data;
    },
    { intervalMs: 30000, deps: [view, tab, country] },
  );

  const activeCountry = useMemo(
    () => countries.find((c) => c.country_code === country),
    [countries, country],
  );

  // The "me" payload key differs between endpoints
  const me = view === "ACTIVE" ? data?.me : data?.me;
  const headerTitle = view === "ACTIVE" ? "Most Active Today" : "Season Leaderboard";
  const headerEyebrow = view === "ACTIVE" ? "Today's grinders" : "Season standings";
  const headerSub = view === "ACTIVE"
    ? <>Ranked by games played since UTC midnight. <span className="text-nk-text">Celebrates dedication separately from raw score.</span> Ties broken by SS earned today.</>
    : <>Ranked by Season Score. Resets every 30 days. <span className="text-nk-text">Championship playoffs currently active in Ghana and Nigeria.</span> Other countries climb for titles and recognition while the league grows.</>;

  return (
    <div className="space-y-8">
      <header>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow mb-2">{headerEyebrow}</p>
            <h1 className="font-display text-4xl font-medium tracking-tight">{headerTitle}</h1>
          </div>
          <LivePill updatedAt={updatedAt} onRefresh={refresh} busy={busy} testid="leaderboard-live" />
        </div>
        <p className="text-nk-mute text-sm mt-3 max-w-2xl">{headerSub}</p>
      </header>

      {/* View toggle — Season vs Active Today */}
      <div className="inline-flex bg-nk-surface border border-nk-line rounded-sm p-0.5" role="tablist">
        {VIEWS.map((v) => (
          <button
            key={v.v}
            role="tab"
            data-testid={`lb-view-${v.testid}`}
            aria-selected={view === v.v}
            onClick={() => setView(v.v)}
            className={`px-4 py-1.5 text-xs uppercase tracking-[0.14em] rounded-sm transition-all inline-flex items-center gap-1.5 ${
              view === v.v
                ? "bg-nk-accent text-nk-bg font-medium"
                : "text-nk-mute hover:text-nk-text"
            }`}
          >
            {v.v === "ACTIVE" && <Fire size={11} weight={view === v.v ? "fill" : "regular"} />}
            {v.l}
          </button>
        ))}
      </div>

      {view === "SEASON" && <SeasonTimeline />}

      {view === "SEASON" && <CountryWar />}

      {/* Ranked-status checklist — soft-gate. Shows on Season view when the
          current user hasn't hit the activity threshold yet. */}
      {view === "SEASON" && me && me.is_ranked === false && (
        <RankedChecklist checklist={me.checklist} />
      )}

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.v}
            data-testid={`lb-tab-${t.testid}`}
            onClick={() => setTab(t.v)}
            className={`px-4 py-2 text-sm border rounded-sm transition-all ${
              tab === t.v
                ? "bg-nk-accent text-nk-bg border-nk-accent font-medium"
                : "border-nk-line text-nk-mute hover:text-nk-text"
            }`}
          >{t.l}</button>
        ))}
        {tab === "COUNTRY" && (
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            data-testid="lb-country-select"
            className="ml-2 bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-accent outline-none"
          >
            {[...countries]
              .sort((a, b) => {
                if (a.playoff_eligible !== b.playoff_eligible) return a.playoff_eligible ? -1 : 1;
                if (a.is_african !== b.is_african) return a.is_african ? -1 : 1;
                return a.country_name.localeCompare(b.country_name);
              })
              .map((c) => (
                <option key={c.country_code} value={c.country_code}>
                  {c.country_name}{c.playoff_eligible ? " · Playoff-active" : ""}
                </option>
              ))}
          </select>
        )}
      </div>

      {tab === "COUNTRY" && activeCountry && (
        <Link
          to={`/country/${activeCountry.country_code}`}
          data-testid={`lb-country-page-link-${activeCountry.country_code.toLowerCase()}`}
          className="nk-card p-4 flex items-center gap-3 hover:border-nk-gold/40 transition-colors group"
        >
          {activeCountry.country_code === "ROW"
            ? <GlobeInline size={20} />
            : <CountryFlag country={activeCountry.country_code} size={22} />}
          <div className="flex-1">
            <p className="font-display text-lg group-hover:text-nk-gold transition-colors">{activeCountry.country_name}</p>
            <p className="text-xs text-nk-dim">
              {activeCountry.playoff_eligible
                ? <><span className="text-nk-gold">Playoff-active country</span> — cash prizes at Day 30</>
                : activeCountry.is_african
                  ? <><span className="text-nk-text">Ranked country</span> — titles + recognition. Cash prizes unlock as the league grows.</>
                  : <>Rest of World — separate ranking track</>}
            </p>
          </div>
          <span className="text-[10px] text-nk-dim group-hover:text-nk-gold transition-colors">Open country page →</span>
        </Link>
      )}

      <div className="nk-card overflow-x-auto" data-testid={`lb-table-${view.toLowerCase()}`}>
        <table className="w-full text-sm">
          <thead className="border-b border-nk-line">
            {view === "ACTIVE" ? (
              <tr>
                <Th>Rank</Th>
                <Th>Player</Th>
                <Th>Country</Th>
                <Th>Games Today</Th>
                <Th>SS Today</Th>
                <Th>Season Score</Th>
                <Th>Rating</Th>
              </tr>
            ) : (
              <tr>
                <Th>Rank</Th>
                <Th>Player</Th>
                <Th>Country</Th>
                <Th>Season Score</Th>
                <Th>Rating</Th>
                <Th>Daily</Th>
                <Th>Ladder</Th>
                <Th>Duels</Th>
              </tr>
            )}
          </thead>
          <tbody>
            {(data?.entries || []).length === 0 && (
              <tr><td colSpan={view === "ACTIVE" ? 7 : 8} className="px-4 py-14 text-center text-nk-mute text-sm">
                {view === "ACTIVE"
                  ? "No games played yet today. Be the first to grind — your name shows up the moment you finish a game."
                  : "No one has cleared the activity threshold yet — play 10 games to appear on this board."}
              </td></tr>
            )}
            {(data?.entries || []).map((r) => {
              const top10 = r.rank <= 10;
              const top3 = r.rank <= 3;
              return (
                <tr key={r.user_id} className={`border-b border-nk-line/50 ${top3 ? "bg-nk-accent/5" : top10 ? "bg-nk-surface/40" : ""}`}>
                  <td className={`px-4 py-3 font-mono ${top3 ? "text-nk-accent font-bold" : top10 ? "text-nk-text" : "text-nk-mute"}`}>
                    {top3 && "★ "}#{r.rank}
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/profile/${r.user_id}`} className="hover:text-nk-accent transition-colors flex items-center gap-2">
                      <AvatarIcon id={r.avatar} size={22} />
                      <span className="font-medium">{r.username || "Player"}</span>
                      {r.current_country_leader_country === r.country && (
                        <span title="Country Leader (L5) — #1 in their country this season"
                              data-testid={`country-leader-crown-${r.user_id}`}>
                          <Crown size={14} weight="fill" className="text-nk-gold" />
                        </span>
                      )}
                      <PremiumBadge active={r.nk_plus_active} size="sm" />
                      {r.unranked && (
                        <span title="Not yet ranked — needs 10+ games and 2+ day-old account"
                              data-testid={`unranked-tag-${r.user_id}`}
                              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-nk-mute/40 text-nk-mute rounded-sm">
                          Unranked
                        </span>
                      )}
                    </Link>
                  </td>
                  <td className="px-4 py-3"><CountryFlag country={r.country} size={14} /></td>
                  {view === "ACTIVE" ? (
                    <>
                      <td className="px-4 py-3 font-mono text-nk-gold font-bold">{(r.games_today || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono text-nk-accent">+{(r.ss_today || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono text-nk-mute">{(r.season_score || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono text-nk-mute">{r.knowledge_rating || 1000}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 font-mono text-nk-accent">{(r.season_score || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono">{r.knowledge_rating || 1000}</td>
                      <td className="px-4 py-3 font-mono text-nk-mute">{r.daily_wins || 0}</td>
                      <td className="px-4 py-3 font-mono text-nk-mute">{r.ladder_wins || 0}</td>
                      <td className="px-4 py-3 font-mono text-nk-mute">{r.duel_wins || 0}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        {me && (
          <div className="border-t-2 border-nk-accent/40 bg-nk-surfaceHi px-4 py-3 flex items-center gap-4 text-sm flex-wrap">
            <span className="eyebrow text-nk-accent">You</span>
            <span className="font-mono">{me.rank ? `#${me.rank}` : "Unranked"}</span>
            <span className="font-medium">{me.username}</span>
            <CountryFlag country={me.country} size={14} />
            <span className="ml-auto font-mono text-nk-accent">
              {view === "ACTIVE"
                ? <>{(me.games_today || 0).toLocaleString()} games today · +{(me.ss_today || 0).toLocaleString()} SS</>
                : <>{(me.season_score || 0).toLocaleString()} pts · {me.knowledge_rating || 1000} rating</>}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-left text-xs text-nk-mute font-medium uppercase tracking-wider">{children}</th>;
}

/**
 * RankedChecklist — soft-gate progress banner shown when the current user
 * hasn't yet met the requirements to be officially "ranked".
 * Auto-dismisses (unmounts) once both checklist items are complete.
 */
function RankedChecklist({ checklist }) {
  if (!checklist) return null;
  const { games, account_age: age } = checklist;
  return (
    <div className="nk-card p-5 border-l-2 border-nk-gold/70" data-testid="ranked-checklist">
      <div className="flex items-start gap-3 flex-wrap">
        <Trophy size={20} weight="fill" className="text-nk-gold mt-0.5 shrink-0" />
        <div className="flex-1 min-w-[220px]">
          <p className="eyebrow text-nk-gold mb-1">Climb to Ranked</p>
          <p className="text-sm text-nk-text mb-3">
            You're on the board! Complete the checklist below to become
            <span className="text-nk-gold font-medium"> officially ranked</span>
            {" "}and lose the "unranked" tag next to your name.
          </p>
          <ul className="space-y-2 text-sm">
            <ChecklistItem
              done={games.done}
              testid="checklist-games"
              label={
                games.done
                  ? `Played ${games.current} games this season ✓`
                  : `Play ${games.remaining} more game${games.remaining === 1 ? "" : "s"} this season`
              }
              progress={`${games.current} / ${games.target}`}
            />
            <ChecklistItem
              done={age.done}
              testid="checklist-age"
              label={
                age.done
                  ? `Account is ${age.current} day${age.current === 1 ? "" : "s"} old ✓`
                  : `Come back in ${age.remaining} day${age.remaining === 1 ? "" : "s"} — account still fresh`
              }
              progress={`${age.current} / ${age.target}d`}
            />
          </ul>
        </div>
      </div>
    </div>
  );
}

function ChecklistItem({ done, label, progress, testid }) {
  return (
    <li className="flex items-center gap-2.5" data-testid={testid}>
      {done
        ? <CheckCircle size={16} weight="fill" className="text-nk-good shrink-0" />
        : <Circle size={16} weight="regular" className="text-nk-mute shrink-0" />}
      <span className={done ? "text-nk-mute line-through" : "text-nk-text"}>{label}</span>
      <span className="ml-auto text-xs text-nk-mute font-mono">{progress}</span>
    </li>
  );
}
